import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type {
  ApiResponse,
  PermissionCatalogSystem,
  ProfilePermission,
  ProfileRequestPayload,
} from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';

interface PageableData {
  content: Record<string, unknown>[];
}

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/profiles`;
  }

  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  mapToProfile(item: Record<string, unknown>): ProfilePermission {
    return {
      id: String(item['id'] ?? ''),
      name: String(item['name'] ?? ''),
      code: item['code'] != null ? String(item['code']) : undefined,
      system: Boolean(item['system']),
      assignedJobs: Number(item['assignedJobs'] ?? 0),
      assignedEmployees: Number(item['assignedEmployees'] ?? 0),
      assignedPermissions: Number(item['assignedPermissions'] ?? 0),
      lastAccess: String(item['lastAccess'] ?? '—'),
      status: this.normalizeStatus(item['status'] as string | undefined),
      systemIds: Array.isArray(item['systemIds'])
        ? (item['systemIds'] as string[])
        : [],
      permissions: (item['permissions'] as Record<string, string[]>) ?? {},
    };
  }

  getAll(
    page = 0,
    size = 500,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<ProfilePermission[]> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);
    if (search?.trim()) params = params.set('search', search.trim());
    if (status?.trim()) params = params.set('status', status.trim());

    return this.httpClient
      .get<ApiResponse<PageableData>>(this.apiUrl, { params })
      .pipe(
        map((response) =>
          (response.data?.content ?? []).map((item) => this.mapToProfile(item)),
        ),
      );
  }

  getById(id: string): Observable<ProfilePermission> {
    return this.httpClient
      .get<ApiResponse<Record<string, unknown>>>(`${this.apiUrl}/${id}`)
      .pipe(map((response) => this.mapToProfile(response.data ?? {})));
  }

  getCatalog(): Observable<PermissionCatalogSystem[]> {
    return this.httpClient
      .get<ApiResponse<{ systems: PermissionCatalogSystem[] }>>(
        `${this.apiUrl}/catalog`,
      )
      .pipe(map((response) => response.data?.systems ?? []));
  }

  create(data: ProfileRequestPayload): Observable<{ message: string }> {
    return this.httpClient
      .post<{ success: boolean; message: string }>(this.apiUrl, data)
      .pipe(
        map((response) => ({
          message: response.message || 'Perfil creado exitosamente',
        })),
      );
  }

  update(
    id: string,
    data: ProfileRequestPayload,
  ): Observable<{ message: string }> {
    return this.httpClient
      .put<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`, data)
      .pipe(
        map((response) => ({
          message: response.message || 'Perfil actualizado exitosamente',
        })),
      );
  }

  delete(id: string): Observable<{ message: string }> {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`)
      .pipe(
        map((response) => ({
          message: response.message || 'Perfil eliminado exitosamente',
        })),
      );
  }

  updateStatus(id: string): Observable<{ message: string }> {
    return this.httpClient
      .patch<{ success: boolean; message: string }>(
        `${this.apiUrl}/${id}/status`,
        null,
      )
      .pipe(
        map((response) => ({
          message: response.message || 'Status actualizado exitosamente',
        })),
      );
  }

  exportPdf(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de perfiles')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(`${this.apiConfig.apiUrl}/export/pdf/profile`, {
      params,
      responseType: 'blob',
    });
  }

  exportExcel(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de perfiles')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(`${this.apiConfig.apiUrl}/export/excel/profile`, {
      params,
      responseType: 'blob',
    });
  }
}
